import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Building2, MapPin, Phone, Mail, Clock, ExternalLink, Apple, Coffee, Cake, Gift, Package } from 'lucide-react'
import { getFoodBanks, MapLocation } from '../api/map'
import { getSupplyRequests, SupplyRequest } from '../api/supplyRequests'
import { resolveImageUrl } from '../utils/imageUrl'
import { useUser } from '../contexts/UserContext'
import { distanceMiles, formatMiles, hasValidCoordinates } from '../utils/geo'
import { updateVolunteerHomeLocation } from '../api/volunteers'
import { formatGeocodableAddress, geocodeAddress } from '../utils/geocoding'
import { getBusinessHoursStatus } from '../utils/businessHours'

type FoodBankWithDistance = MapLocation & { distanceMiles?: number | null }

const FoodBanksPage = () => {
  const { user, refreshUser, isAuthenticated } = useUser()
  const [foodBanks, setFoodBanks] = useState<MapLocation[]>([])
  const [supplyRequests, setSupplyRequests] = useState<SupplyRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingLocation, setSavingLocation] = useState(false)
  const [locationMessage, setLocationMessage] = useState<string | null>(null)
  const [statusNow, setStatusNow] = useState(() => new Date())
  const [addressCoordinates, setAddressCoordinates] = useState<{
    latitude: number
    longitude: number
  } | null>(null)

  const homeLat = hasValidCoordinates(user?.homeLatitude, user?.homeLongitude)
    ? user?.homeLatitude
    : addressCoordinates?.latitude
  const homeLng = hasValidCoordinates(user?.homeLatitude, user?.homeLongitude)
    ? user?.homeLongitude
    : addressCoordinates?.longitude
  const hasHome = hasValidCoordinates(homeLat, homeLng)

  useEffect(() => {
    if (
      hasValidCoordinates(user?.homeLatitude, user?.homeLongitude) ||
      !user?.street ||
      !user?.city ||
      !user?.state
    ) {
      setAddressCoordinates(null)
      return
    }

    let cancelled = false
    geocodeAddress(formatGeocodableAddress(user)).then((coordinates) => {
      if (!cancelled) setAddressCoordinates(coordinates)
    })
    return () => {
      cancelled = true
    }
  }, [
    user?.homeLatitude,
    user?.homeLongitude,
    user?.street,
    user?.city,
    user?.state,
    user?.zipCode
  ])

  useEffect(() => {
    const fetchFoodBanks = async () => {
      try {
        setLoading(true)
        const banks = await getFoodBanks()
        setFoodBanks(banks)

        if (isAuthenticated) {
          const requests = await getSupplyRequests()
          setSupplyRequests(requests)
        } else {
          setSupplyRequests([])
        }
      } catch (err) {
        setError('Failed to load food banks')
        console.error('Error fetching food banks:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchFoodBanks()
  }, [isAuthenticated])

  useEffect(() => {
    const timer = window.setInterval(() => setStatusNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const rankedFoodBanks: FoodBankWithDistance[] = useMemo(() => {
    const withDistance = foodBanks.map((bank) => {
      if (
        !hasHome ||
        homeLat == null ||
        homeLng == null ||
        !hasValidCoordinates(bank.latitude, bank.longitude)
      ) {
        return { ...bank, distanceMiles: null as number | null }
      }
      return {
        ...bank,
        distanceMiles: distanceMiles(homeLat, homeLng, bank.latitude, bank.longitude)
      }
    })

    if (!hasHome) return withDistance

    return [...withDistance].sort((a, b) => {
      const da = typeof a.distanceMiles === 'number' ? a.distanceMiles : Number.POSITIVE_INFINITY
      const db = typeof b.distanceMiles === 'number' ? b.distanceMiles : Number.POSITIVE_INFINITY
      return da - db
    })
  }, [foodBanks, hasHome, homeLat, homeLng])

  const formatAddress = (foodBank: MapLocation) => {
    return `${foodBank.street}, ${foodBank.city}, ${foodBank.state} ${foodBank.zipCode}`
  }

  const getRequestsForFoodBank = (foodBankId: string) => {
    return supplyRequests
      .filter((sr) => sr.supplierId === foodBankId)
      .filter((sr) => sr.status === 'NEW' || sr.status === 'ACCEPTED')
      .slice(0, 3)
  }

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationMessage('Geolocation is not supported by your browser.')
      return
    }
    setSavingLocation(true)
    setLocationMessage(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          if (user?.userType === 'VOLUNTEER') {
            await updateVolunteerHomeLocation(pos.coords.latitude, pos.coords.longitude)
            await refreshUser()
          }
          setLocationMessage('Location saved. Food banks are ranked nearest first.')
        } catch (e: any) {
          setLocationMessage(e?.message || 'Failed to save location.')
        } finally {
          setSavingLocation(false)
        }
      },
      () => {
        setSavingLocation(false)
        setLocationMessage('Unable to get your location. Please allow location access and try again.')
      },
      { enableHighAccuracy: false, timeout: 10000 }
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading food banks...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading food banks</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0">
        <Apple className="absolute top-20 left-8 h-8 w-8 text-red-500 animate-pulse" />
        <Coffee className="absolute top-40 right-12 h-6 w-6 text-amber-600 animate-bounce" />
        <Cake className="absolute bottom-40 left-16 h-10 w-10 text-orange-400 animate-pulse" />
        <Gift className="absolute bottom-20 right-24 h-6 w-6 text-green-500 animate-bounce" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Building2 className="h-8 w-8 text-primary-600 mr-3" />
              {hasHome ? 'Nearby Food Banks' : 'Food Banks'}
              <Apple className="ml-3 h-6 w-6 text-red-500" />
            </h1>
            <p className="text-gray-600 mt-1">
              {hasHome
                ? `Ranked by distance from your ${
                    user?.userType === 'SUPPLIER' ? 'saved business location' : 'home location'
                  }.`
                : 'Find and connect with local food banks. Add a valid location to rank them by distance.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {!hasHome && user?.userType === 'VOLUNTEER' && (
              <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={savingLocation}
                className="btn-outline disabled:opacity-60"
              >
                {savingLocation ? 'Saving…' : 'Use my location'}
              </button>
            )}
            <Link to="/nearby-map" className="btn-primary inline-flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              View on map
            </Link>
          </div>
        </div>

        {locationMessage && (
          <p className="mb-4 text-sm text-gray-700">{locationMessage}</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rankedFoodBanks.map((foodBank) => {
            const operatingStatus = getBusinessHoursStatus(foodBank.hours, statusNow)
            const statusClass = {
              open: 'bg-green-100 text-green-800',
              'closing-soon': 'bg-amber-100 text-amber-800',
              closed: 'bg-gray-100 text-gray-700',
              'check-website': 'bg-blue-100 text-blue-800'
            }[operatingStatus.status]

            return (
            <div key={foodBank.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow relative overflow-hidden">
              {foodBank.profileImage ? (
                <div className="h-44 w-full overflow-hidden bg-neutral-100">
                  <img
                    src={resolveImageUrl(foodBank.profileImage)}
                    alt={foodBank.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                </div>
              ) : null}
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <Building2 className="h-8 w-8 text-primary-600 mr-3 shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{foodBank.name}</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                        <Clock className="h-3 w-3 mr-1" />
                        {operatingStatus.label}
                      </span>
                      {typeof foodBank.distanceMiles === 'number' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-800">
                          {formatMiles(foodBank.distanceMiles)} away
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                    {formatAddress(foodBank)}
                  </div>
                  {foodBank.phone && (
                    <a
                      href={`tel:${foodBank.phone}`}
                      className="flex items-center text-sm text-gray-600 hover:text-primary-700"
                    >
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      {foodBank.phone}
                    </a>
                  )}
                  {foodBank.email && (
                    <a
                      href={`mailto:${foodBank.email}`}
                      className="flex items-center text-sm text-gray-600 hover:text-primary-700 break-all"
                    >
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {foodBank.email}
                    </a>
                  )}
                  {foodBank.hours && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      {foodBank.hours}
                    </div>
                  )}
                  {foodBank.contactUrl && (
                    <a
                      href={foodBank.contactUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-lg bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visit Website
                    </a>
                  )}
                </div>

                {isAuthenticated && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      <Package className="h-4 w-4 mr-2 text-primary-600" />
                      Current needs
                    </div>
                    <span className="text-xs text-gray-500">
                      {getRequestsForFoodBank(foodBank.id).length} active
                    </span>
                  </div>
                  {getRequestsForFoodBank(foodBank.id).length === 0 ? (
                    <p className="text-sm text-gray-500">No active requests right now.</p>
                  ) : (
                    <div className="space-y-2">
                      {getRequestsForFoodBank(foodBank.id).map((sr) => (
                        <div key={sr.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-start gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{sr.title}</p>
                              <p className="text-xs text-gray-600 line-clamp-2 mt-1">{sr.description}</p>
                            </div>
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                              {sr.urgency}
                            </span>
                          </div>
                          {sr.items && sr.items.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                              Items: {sr.items.slice(0, 3).map(i => i.name).join(', ')}{sr.items.length > 3 ? '…' : ''}
                            </p>
                          )}
                          {sr.expiresAt && (
                            <p className="text-xs text-amber-700 mt-2">
                              Listing expires {new Date(sr.expiresAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

              </div>
            </div>
            )
          })}
        </div>

        {rankedFoodBanks.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No food banks found</h3>
            <p className="text-gray-600">Check back later or contact us to add your food bank.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default FoodBanksPage
